import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle } from 'lucide-react';
import { mockData } from '../mockData';

const FAQPage = () => {
  const { t } = useTranslation();
  const [heroSlide, setHeroSlide] = useState(0);
  
  // Hero carousel images
  const heroSlides = [
    "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&q=90", // Planning
    "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=90", // Questions
    mockData.drcImages.city2, // DRC city
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=90"  // Analysis
  ];
  
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);
  const faqs = [
    {
      question: t('faq.question1'),
      answer: t('faq.answer1')
    },
    {
      question: t('faq.question2'),
      answer: t('faq.answer2')
    },
    {
      question: t('faq.question3'),
      answer: t('faq.answer3')
    },
    {
      question: t('faq.question4'),
      answer: t('faq.answer4')
    },
    {
      question: t('faq.question5'),
      answer: t('faq.answer5')
    },
    {
      question: t('faq.question6'),
      answer: t('faq.answer6')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Premium Hero Section with Carousel */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[500px]">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={heroSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[heroSlide]})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/70 to-blue-800/60"></div>
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Content */}
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-6 py-2 mb-6">
              <HelpCircle className="w-4 h-4 mr-2" />
              {t('faq.badge')}
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">{t('faq.title')}</h1>
            <p className="text-2xl text-blue-100 font-light">
              {t('faq.subtitle')}
            </p>
            
            {/* Slide Indicators */}
            <div className="flex gap-2 justify-center pt-8">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setHeroSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === heroSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('faq.contentTitle')}</h2>
            <p className="text-xl text-gray-600">
              {t('faq.contentSubtitle')}
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border-2 border-gray-200 rounded-xl px-6 hover:border-blue-500 transition-colors"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-blue-900 hover:text-blue-700 py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                  {faq.answer.split('\n\n').map((paragraph, pIndex) => (
                    <p key={pIndex} className="mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
